import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: object;
  keywords?: string; // 👈 New: Keywords for meta tags
  noindex?: boolean; // 👈 New: Control indexing
  section?: string; // 👈 New: Article section
  tags?: string[]; // 👈 New: Article tags
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  image,
  url,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  structuredData,
  keywords,
  noindex = false,
  section,
  tags
}) => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://m2mba.com';
  const fullUrl = url?.startsWith('http') ? url : `${siteUrl}${url || ''}`;
  const fullImageUrl = image && !image.startsWith('http') ? `${siteUrl}${image}` : image;
  
  // Ensure we have a fallback image for social sharing
  const socialImageUrl = fullImageUrl || `${siteUrl}/images/og-default.jpg`;
  
  // Default keywords for Vietnamese market
  const defaultKeywords = 'business analysis, đào tạo BA, M2MBA, phân tích kinh doanh, training center, khoá học BA, AI, automation';
  const finalKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "M2MBA Academy",
    "url": "https://m2mba.com",
    "logo": "https://storage.googleapis.com/m2mba/f4872d86-57b4-4bb8-9492-f14a60f22280.jpg",
    "description": "Trung tâm M2MBA chuyên đào tạo Business Analyst, AI, Automation - Đồng hành cùng học viên trong việc phát triển sự nghiệp.",
    "foundingDate": "2019",
    "sameAs": [
      "https://www.facebook.com/profile.php?id=100063494024929",
      "https://zalo.me/0983213076"
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title || 'M2MBA - Trung tâm đào tạo Business Analyst'}</title>
      <meta name="description" content={description || 'Khóa học BA, AI, Automation từ cơ bản đến nâng cao, đồng hành cùng học viên chinh phục nghề Business Analyst.'} />
      <meta name="keywords" content={finalKeywords} />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}
      
      {/* Vietnamese market optimization */}
      <meta name="geo.region" content="VN" />
      <meta name="geo.country" content="Vietnam" />
      <meta name="language" content="vi" />
      
      {/* Author */}
      {author && <meta name="author" content={author} />}

      {/* Open Graph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={socialImageUrl} />
      <meta property="og:image:secure_url" content={socialImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - M2MBA Training Center`} />
      <meta property="og:site_name" content="M2MBA Training Center" />
      <meta property="og:locale" content="vi_VN" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={socialImageUrl} />
      <meta name="twitter:image:alt" content={`${title} - M2MBA Training Center`} />
      <meta name="twitter:site" content="@m2mba" />
      <meta name="twitter:creator" content="@m2mba" />

      {/* Article Specific Tags */}
      {type === 'article' && author && <meta property="article:author" content={author} />}
      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {type === 'article' && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {type === 'article' && section && <meta property="article:section" content={section} />}
      {type === 'article' && tags && tags.map(tag => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Mobile Optimization */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      
      {/* DNS Prefetch for Performance */}
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="dns-prefetch" href="//storage.googleapis.com" />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
